import { Component, computed, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { CustomFilterService } from '../../../../core/services/custom-filter.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IDropdownOption } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { CustomFiltersAddEditDialogComponent } from './custom filters-add-edit-dialog/custom-filters-add-edit-dialog.component';
import { ICustomFilter, ICustomFilterAddEditDialogData } from './custom-filters.interface';
import { CustomFiltersStore } from './custom-filters.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [CustomFiltersStore],
  selector: 'app-custom-filters',
  styleUrl: './custom-filters.component.scss',
  templateUrl: './custom-filters.component.html',
})
export class CustomFiltersComponent
  extends BaseCrudPageComponent<ICustomFilter, IBaseDatatableState<ICustomFilter>>
  implements OnInit
{
  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(({ id, name }) => ({
      id,
      name,
    }));
  });

  public readonly datatableColumns: IDatatableColumn<IDropdownOption>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  public datatableConfigurations: IDatatableConfiguration<IDropdownOption> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: CustomFiltersStore,
    public readonly formService: FormUtilitiesService,
    public readonly customFilterService: CustomFilterService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfCustomFilter',
      multiContext: 'field.customFilters',
      nameProperty: 'name',
      singleContext: 'field.customFilter',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        this.store.loadData({
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: ICustomFilterAddEditDialogData = {
      dialogTitle: this.addEditModalTitle(),
      submitButtonText: this.addEditSubmitButtonText(),
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(CustomFiltersAddEditDialogComponent, {
          ...CustomFiltersAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }

  public override initializeDelete(): void {
    const [selectedRow] = this.selectedRows();

    if (!selectedRow) {
      return;
    }

    const doYouWantToDeleteMessage = this.translate.instant('system.delete.confirmationSingleContent', {
      context: selectedRow[this.context.nameProperty],
    }) as string;

    this.customFilterService.getInUseData(selectedRow.id).subscribe(({ data }) => {
      const warnings = Object.entries(data).map(([errorKey, context]) => {
        const contextJoined = context.join(', ');
        const errorMessage = this.translate.instant(`page.settings.customFilter.${errorKey}`);

        return `
          <span class="info-message">
            <div class="row">
              <div class="col-12 m-auto">${errorMessage} ${contextJoined}</div>
            </div></span
          >
        `;
      });

      const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
        data: {
          cancelButtonText: this.translate.instant('button.cancel'),
          content: `
          ${warnings.join('</br>')}
          </br>
          <div>${doYouWantToDeleteMessage}</div>
        `,
          submitButtonText: this.translate.instant('button.delete'),
          title: this.translate.instant('system.delete.confirmationTitle', {
            context: this.translate.instant(this.context.singleContext),
          }),
        },
      });

      this.subscriptions.push(
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.datatableStore.deleteOne(selectedRow.id);
          }
        }),
      );
    });
  }
}
